// ABOUTME: Hook loading one server-side page of a find & replace run's rows (GET /runs/:runId/rows).
// ABOUTME: Refetches when the run, the filter or the page changes; stale answers of older requests are dropped.

import { useCallback, useEffect, useRef, useState } from 'react';

import { getFindReplaceRows } from '../api/findReplaceApi';
import { FindReplaceRowDto, RowsFilter, RowsPaging } from '../types';

/** The server rejects larger pages. */
export const MAX_ROWS_PAGE_SIZE = 200;

export function useFindReplaceRows(runId: string | null, filter: RowsFilter, paging: RowsPaging) {
    const [rows, setRows] = useState<FindReplaceRowDto[]>([]);
    const [count, setCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const requestId = useRef(0);

    // Callers usually build the filter inline: the serialized form keeps the fetch stable.
    const filterKey = JSON.stringify(filter);
    const { skip } = paging;
    const limit = Math.min(paging.limit, MAX_ROWS_PAGE_SIZE);

    const refetch = useCallback(async () => {
        if (!runId) return;
        requestId.current += 1;
        const thisRequest = requestId.current;
        setLoading(true);
        try {
            const data = await getFindReplaceRows(runId, JSON.parse(filterKey), { skip, limit });
            if (thisRequest === requestId.current) {
                setRows(data.rows);
                setCount(data.count);
                setError(null);
            }
        } catch (e) {
            if (thisRequest === requestId.current) {
                setError(e instanceof Error ? e : new Error(String(e)));
            }
        } finally {
            if (thisRequest === requestId.current) setLoading(false);
        }
    }, [runId, filterKey, skip, limit]);

    useEffect(() => {
        refetch();
    }, [refetch]);

    return { rows, count, loading, error, refetch };
}
